import React, { useState, useEffect, useRef } from 'react'

import { getAddresses } from '../../../../critical/services/address'
import useClickAway from '~/hooks/useClickAway'
import { isValidPostcode, formatPostcode } from '~/utils/postcode'

import styles from './address-finder.module.css'
import { FormError } from './form-error'
import { Spinner } from './spinner'

export const AddressFinder = ({ address, onAddressChange, addressError }) => {
  const { ref, active, setActive } = useClickAway()

  const postcodeInputRef = useRef()
  const [postcode, setPostcode] = useState(address?.postcode || '')
  const [formattedAddress, setFormattedAddress] = useState(address?.summaryLine || '')
  const [addresses, setAddresses] = useState(null)
  const [fetchError, setFetchError] = useState(false)

  const shouldShowDropdownLoader = isValidPostcode(postcode) && addresses === null && !fetchError

  useEffect(() => {
    const alreadyHasAddresses = postcode === addresses?.[0]?.postcode

    if (isValidPostcode(postcode) && !fetchError && active && !alreadyHasAddresses) {
      setAddresses(null)
      fetchAddressOptions()
    }
  }, [postcode, active])

  useEffect(() => {
    if (postcode && formattedAddress === '') {
      postcodeInputRef.current.focus()
    }
  }, [formattedAddress])

  const postcodeFocusHandler = () => {
    setActive(isValidPostcode(postcode))
  }

  const postcodeChangeHandler = async ({ target: { value } }) => {
    const isValueValid = isValidPostcode(value)

    setActive(isValueValid)
    setPostcode(isValueValid ? formatPostcode(value) : value.toUpperCase())
    setFetchError(false)

    if (!isValueValid) {
      setAddresses(null)
    }
  }

  const addressChangeHandler = (a) => () => {
    onAddressChange(a)
    setFormattedAddress(a.summaryLine)
    setActive(false)
  }

  const editPostcodeClickHandler = () => {
    setFormattedAddress('')
    onAddressChange({ postcode })
  }

  const clearClickHandler = () => {
    setActive(false)
    setPostcode('')
    setAddresses(null)
    onAddressChange(null)
  }

  const fetchAddressOptions = async () => {
    try {
      setAddresses(await getAddresses(postcode))
    } catch (e) {
      console.error(e)
      setFetchError(true)
    }
  }

  const renderAddressOption = (option) => (
    <button
      data-cypress-id='address-list-item'
      key={option.summaryLine}
      className={`${styles.dropdownItem} fs-mask`}
      onClick={addressChangeHandler(option)}
    >
      {option.summaryLine}
    </button>
  )

  return (
    <div className={styles.addressFinder}>
      <svg className={styles.iconPin} height={24} width={24}>
        <use xlinkHref='/icons.svg#icon-pin' />
      </svg>

      {!formattedAddress && (
        <>
          <div ref={ref}>
            <input
              id='postcode-input'
              name='postcode'
              ref={postcodeInputRef}
              type='text'
              autoComplete='off'
              className={styles.addressFinderInput}
              data-postcode={!!postcode}
              data-error={!!addressError || fetchError}
              value={postcode}
              placeholder='Enter your postcode'
              onFocus={postcodeFocusHandler}
              onClick={postcodeFocusHandler}
              onChange={postcodeChangeHandler}
            />

            {postcode && (
              <button onClick={clearClickHandler} className={styles.iconButton}>
                <svg className={styles.icon} height={24} width={24}>
                  <use xlinkHref='/icons.svg#icon-close' />
                </svg>
              </button>
            )}

            {active && (
              <div className={styles.dropdownContainer}>
                <div className={styles.dropdown}>
                  {shouldShowDropdownLoader && (
                    <div className={styles.loadingAddresses}>
                      <p className={styles.dropdownLoadingText}>Looking for addresses...</p>
                      <Spinner />
                    </div>
                  )}

                  {fetchError && (
                    <>
                      <div className={styles.dropdownErrorHeading}>
                        <svg className={styles.icon} height={24} width={24}>
                          <use xlinkHref='/icons.svg#icon-info-o' />
                        </svg>
                        Sorry, we couldn't find any addresses.
                      </div>
                      <div className={styles.dropdownErrorText}>Check your postcode and try again.</div>
                    </>
                  )}

                  {addresses?.length && (
                    <ul className={styles.dropdownItems}>{addresses.map((address) => renderAddressOption(address))}</ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {formattedAddress && (
        <button onClick={editPostcodeClickHandler} className={`${styles.addressButton} fs-mask`}>
          {formattedAddress}
        </button>
      )}

      {/* Input field below used for tracking only */}
      <input data-nerd-capture id='current-address' name='Current address' value={formattedAddress} type='hidden' />

      {addressError && <FormError text={addressError} />}
    </div>
  )
}
